import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useDemo } from '../../contexts/DemoContext';
import { useInvestments } from '../../hooks/useInvestments';
import { useTour } from '../../contexts/TourContext';
import VideoModal from '../../components/ui/VideoModal';
import { getVideoForPage } from '../../config/videoConfig';
import DemoModeBanner from '../../components/demo/DemoModeBanner';
import InvestmentModal from './components/InvestmentModal';
import InvestmentTimelineChart from './components/InvestmentTimelineChart';
import AssetClassDistributionChart from './components/AssetClassDistributionChart';
import { formatCurrencyINR } from '../../utils/currency';

const InvestmentsAllocations = () => {
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingInvestment, setEditingInvestment] = useState(null);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', assetType: null });
  
  // Use the investments hook for API integration
  const { 
    investments, 
    loading, 
    error, 
    createInvestment, 
    updateInvestment, 
    deleteInvestment, 
    calculateSummary 
  } = useInvestments();
  const { canWrite } = useSubscription();
  const { isDemoModeActive } = useDemo();
  const effectiveCanWrite = canWrite && !isDemoModeActive;
  const { startInvestmentsTour, autoStartTour } = useTour();
  const videoConfig = getVideoForPage('investments-allocations');

  // Auto-start tour for first-time visitors
  useEffect(() => {
    if (!loading && investments) {
      autoStartTour('investments-allocations', startInvestmentsTour);
    }
  }, [loading, investments, autoStartTour, startInvestmentsTour]);

  // Get unique asset classes for categories
  const assetClasses = ['All', ...new Set(investments.map(inv => inv.type).filter(Boolean))];
  
  const investmentCategories = assetClasses.map(assetClass => ({
    name: assetClass,
    count: assetClass === 'All' 
      ? investments.length 
      : investments.filter(inv => inv.type === assetClass).length
  }));

  // Calculate summary using the hook
  const summary = calculateSummary();
  const { totalAnnualInvestment, activeInvestments, avgExpectedReturn, byAssetClass } = summary;

  const formatCurrency = (amount) => {
    if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(1)}L`;
    }
    return `₹${(amount / 1000).toFixed(0)}K`;
  };

  const formatLargeCurrency = (amount) => {
    if (amount >= 10000000) {
      return `₹${(amount / 10000000).toFixed(2)}Cr`;
    }
    if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(2)}L`;
    }
    return `₹${(amount / 1000).toFixed(0)}K`;
  };

  const handleAddInvestment = async (investmentData) => {
    try {
      const result = await createInvestment(investmentData);
      
      // Check if a new asset was auto-created
      if (result?.assetCreated && result?.createdAssetType) {
        setNotification({
          show: true,
          message: `A new asset type "${result.createdAssetType}" has been automatically added to your portfolio.`,
          assetType: result.createdAssetType
        });
        
        // Auto-hide notification after 5 seconds
        setTimeout(() => {
          setNotification({ show: false, message: '', assetType: null });
        }, 5000);
      }
    } catch (error) {
      console.error('Error adding investment:', error);
    }
  };

  const handleEditInvestment = async (investmentData) => {
    try {
      await updateInvestment(investmentData.id, investmentData);
      setEditingInvestment(null);
    } catch (error) {
      console.error('Error updating investment:', error);
    }
  };

  const handleDeleteInvestment = async (investmentId) => {
    try {
      const result = await deleteInvestment(investmentId);
      if (result === true) {
        console.log('Investment deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting investment:', error);
    }
  };

  const handleSaveInvestment = async (investmentData) => {
    if (editingInvestment) {
      await handleEditInvestment(investmentData);
    } else {
      await handleAddInvestment(investmentData);
    }
  };

  // Filter investments based on active category
  const filteredInvestments = activeCategory === 'All' 
    ? investments 
    : investments.filter(inv => inv.type === activeCategory);

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading investment information...</p>
        </div>
      </div>
    );
  }

  // Don't show error state - just show empty state instead
  // The hook now handles 404 gracefully by returning empty array

  return (
    <div className="min-h-screen bg-background">
      <Sidebar 
        isExpanded={sidebarExpanded} 
        onToggle={() => setSidebarExpanded(!sidebarExpanded)} 
      />
      
      <Header 
        sidebarExpanded={sidebarExpanded}
        onSidebarToggle={() => setSidebarExpanded(!sidebarExpanded)}
      />

      <DemoModeBanner />

      <main 
        className={`
          transition-all duration-300 ease-spring pt-16 pb-20 lg:pb-6
          ${sidebarExpanded ? 'lg:ml-64' : 'lg:ml-16'}
        `}
      >
        <div className="p-4 sm:p-6">
          <Breadcrumb />
          
          {/* Notification Banner */}
          {notification.show && (
            <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex items-start gap-3 animate-in slide-in-from-top-2">
              <Icon name="Info" size={20} className="text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  {notification.message}
                </p>
                {notification.assetType && (
                  <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                    You can view and manage this asset in the <strong>Asset Portfolio</strong> section.
                  </p>
                )}
              </div>
              <button
                onClick={() => setNotification({ show: false, message: '', assetType: null })}
                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 shrink-0"
                aria-label="Close notification"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
          )}
          
          {/* Page Header */}
          <div className="mb-8" data-tour="investments-header">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">Investments & Allocations</h1>
                <p className="text-muted-foreground">
                  Track recurring investments and their allocation across asset classes.
                </p>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" iconName="Video" iconPosition="left" onClick={() => setIsVideoOpen(true)} className="hidden sm:inline-flex">
                  Learn
                </Button>
                <Button variant="outline" iconName="HelpCircle" iconPosition="left" onClick={startInvestmentsTour} className="hidden sm:inline-flex">
                  Tour
                </Button>
                <Button iconName="Plus" iconPosition="left" onClick={() => setIsAddModalOpen(true)} disabled={!effectiveCanWrite}>
                  Add Investment
                </Button>
              </div>
            </div>
          </div>

          {/* Key Metrics Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-tour="investments-summary">
            {/* Total Annual Investment */}
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="TrendingUp" size={20} className="text-primary" />
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">Total Annual Investment</p>
                <p className="text-2xl font-bold text-foreground">{formatLargeCurrency(totalAnnualInvestment)}</p>
                <p className="text-sm text-muted-foreground">{activeInvestments} active investments</p>
              </div>
            </div>

            {/* Active Investments */}
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                  <Icon name="PieChart" size={20} className="text-accent" />
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">Active Investments</p>
                <p className="text-2xl font-bold text-foreground">{activeInvestments}</p>
                <p className="text-sm text-muted-foreground">{investments.length} total</p>
              </div>
            </div>

            {/* Average Expected Return */}
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
                  <Icon name="Percent" size={20} className="text-warning" />
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">Avg Expected Return</p>
                <p className="text-2xl font-bold text-foreground">{avgExpectedReturn.toFixed(1)}%</p>
                <p className="text-sm text-muted-foreground">Annualized</p>
              </div>
            </div>

            {/* Asset Classes */}
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                  <Icon name="Layers" size={20} className="text-success" />
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">Asset Classes</p>
                <p className="text-2xl font-bold text-foreground">{summary.assetClasses.length}</p>
                <p className="text-sm text-muted-foreground">Diversified</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            {/* Left Column - Main Content */}
            <div className="xl:col-span-2 space-y-8">
              {/* Investment Categories */}
              <div className="bg-card rounded-lg border border-border p-6" data-tour="investments-table">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="List" size={16} className="text-primary" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">Investment Categories</h2>
                    <p className="text-sm text-muted-foreground">{filteredInvestments.length} investments in all categories</p>
                  </div>
                </div>

                {/* Investment Category Tabs */}
                <div className="flex flex-wrap gap-2 mb-6" data-tour="investments-categories">
                  {investmentCategories.map(category => {
                    const isActive = activeCategory === category.name;
                    
                    return (
                      <button
                        key={category.name}
                        onClick={() => setActiveCategory(category.name)}
                        className={`
                          flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
                          ${isActive 
                            ? 'bg-primary text-primary-foreground shadow-soft' 
                            : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
                          }
                        `}
                      >
                        <Icon 
                          name="PieChart" 
                          size={16} 
                        />
                        {category.name}
                        <span className={`
                          px-2 py-0.5 rounded-full text-xs
                          ${isActive 
                            ? 'bg-primary-foreground/20 text-primary-foreground' 
                            : 'bg-background text-muted-foreground'
                          }
                        `}>
                          {category.count}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Investment Table */}
                {filteredInvestments.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                            <div className="flex items-center gap-2">
                              INVESTMENT NAME
                              <Icon name="ArrowUpDown" size={14} className="text-muted-foreground" />
                            </div>
                          </th>
                          <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                            <div className="flex items-center gap-2">
                              ASSET CLASS
                              <Icon name="ArrowUpDown" size={14} className="text-muted-foreground" />
                            </div>
                          </th>
                          <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                            <div className="flex items-center gap-2">
                              ANNUAL AMOUNT
                              <Icon name="ChevronDown" size={14} className="text-muted-foreground" />
                            </div>
                          </th>
                          <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                            <div className="flex items-center gap-2">
                              FREQUENCY
                              <Icon name="ArrowUpDown" size={14} className="text-muted-foreground" />
                            </div>
                          </th>
                          <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                            EXPECTED RETURN
                          </th>
                          <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                            ACTIONS
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredInvestments.map(investment => {
                          const freq = investment.frequency || 'monthly';
                          const amount = investment.amount || 0;
                          let annualAmount = 0;
                          
                          if (freq === 'monthly') annualAmount = amount * 12;
                          else if (freq === 'quarterly') annualAmount = amount * 4;
                          else if (freq === 'annually') annualAmount = amount;
                          else if (freq === 'one-time') annualAmount = amount;
                          
                          return (
                            <tr key={investment.id} className="border-b border-border/50 hover:bg-muted/30">
                              <td className="py-4 px-4">
                                <div className="flex items-center gap-3">
                                  <Icon 
                                    name="TrendingUp" 
                                    size={16} 
                                    className="text-muted-foreground"
                                  />
                                  <div>
                                    <div className="font-medium text-foreground">{investment.name}</div>
                                    {investment.priority && (
                                      <div className="text-xs text-muted-foreground">Priority: {investment.priority}</div>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="py-4 px-4">
                                <span className="text-sm text-foreground">{investment.type}</span>
                              </td>
                              <td className="py-4 px-4">
                                <div>
                                  <div className="font-medium text-foreground">{formatCurrency(annualAmount)}</div>
                                  <div className="text-sm text-muted-foreground">
                                    {freq === 'monthly' ? `${formatCurrency(amount)}/month` :
                                     freq === 'quarterly' ? `${formatCurrency(amount)}/quarter` :
                                     freq === 'annually' ? `${formatCurrency(amount)}/year` :
                                     'One-time'}
                                  </div>
                                </div>
                              </td>
                              <td className="py-4 px-4">
                                <span className="text-sm text-muted-foreground capitalize">{freq}</span>
                              </td>
                              <td className="py-4 px-4">
                                <div>
                                  <div className="font-medium text-foreground">{investment.expectedReturn}%</div>
                                  <div className="text-sm text-muted-foreground">
                                    {investment.autoReinvest ? 'Auto Reinvest' : 'Returns to Cash'}
                                  </div>
                                </div>
                              </td>
                              <td className="py-4 px-4">
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setEditingInvestment(investment);
                                      setIsAddModalOpen(true);
                                    }}
                                    className="h-8 w-8 p-0"
                                  >
                                    <Icon name="Edit2" size={14} />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      if (window.confirm(`Are you sure you want to delete "${investment.name}"?`)) {
                                        handleDeleteInvestment(investment.id);
                                      }
                                    }}
                                    className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                  >
                                    <Icon name="Trash2" size={14} />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Icon name="TrendingUp" size={48} className="text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-foreground mb-2">
                      {activeCategory === 'All' ? 'No investments added yet' : `No ${activeCategory} investments found`}
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      {activeCategory === 'All' 
                        ? 'Start by adding your first recurring investment' 
                        : `Add your first ${activeCategory.toLowerCase()} investment to get started`
                      }
                    </p>
                    <Button onClick={() => setIsAddModalOpen(true)}>
                      Add {activeCategory === 'All' ? 'Investment' : 'Investment'}
                    </Button>
                  </div>
                )}
              </div>

              {/* Investment Timeline Chart */}
              <div className="bg-card rounded-lg border border-border p-6" data-tour="investments-chart">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name="TrendingUp" size={16} className="text-primary" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-foreground">Investment Timeline</h2>
                      <p className="text-sm text-muted-foreground">
                        Track your investment contributions over time
                      </p>
                    </div>
                  </div>
                </div>

                <InvestmentTimelineChart investments={investments} />
              </div>
            </div>

            {/* Right Column - Sidebar */}
            <div className="xl:col-span-1 space-y-8">
              {/* Asset Class Distribution */}
              <div className="bg-card rounded-lg border border-border p-6" data-tour="investments-distribution">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="PieChart" size={16} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">Asset Class Distribution</h3>
                    <p className="text-sm text-muted-foreground">Current year allocation</p>
                  </div>
                </div>

                <AssetClassDistributionChart investments={investments} />
              </div>

              {/* Investment Overview */}
              <div className="bg-card rounded-lg border border-border p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Info" size={16} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">Investment Overview</h3>
                    <p className="text-sm text-muted-foreground">Summary of your investments</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Total Annual Investment</span>
                    <span className="font-medium text-foreground">{formatLargeCurrency(totalAnnualInvestment)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Active Investments</span>
                    <span className="font-medium text-foreground">{activeInvestments}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Avg Expected Return</span>
                    <span className="font-medium text-foreground">{avgExpectedReturn.toFixed(1)}%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Asset Classes</span>
                    <span className="font-medium text-foreground">{summary.assetClasses.length}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Investment Modal */}
      <InvestmentModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setEditingInvestment(null);
        }}
        onSave={handleSaveInvestment}
        editingInvestment={editingInvestment}
      />

      {/* Video Modal */}
      <VideoModal
        isOpen={isVideoOpen}
        onClose={() => setIsVideoOpen(false)}
        videoUrl={videoConfig?.url}
        title={videoConfig?.title}
      />
    </div>
  );
};

export default InvestmentsAllocations;

